import { t } from "@travily/i18n";
import { StickerStarIcon } from "@ui/components/Icon";
import { tv } from "@ui/lib/tv";

const rating = tv({
  slots: {
    root: "flex items-center gap-1",
    stars: "flex items-center",
    star: "rotate-[10deg]",
    score: "leading-6 text-neutral-600 [font-variant-ligatures:none]",
    reviewCount: "leading-6 text-neutral-600 [font-variant-ligatures:none]",
  },
  variants: {
    size: {
      "20": { star: "size-5", score: "text-base", reviewCount: "text-base" },
      "24": { star: "size-6", score: "text-lg", reviewCount: "text-lg" },
    },
    variant: {
      full: {},
      compact: { score: "font-normal", reviewCount: "font-normal" },
    },
  },
  compoundVariants: [
    {
      variant: "compact",
      size: "20",
      class: { score: "font-semibold", reviewCount: "text-neutral-400" },
    },
  ],
  defaultVariants: { size: "24", variant: "full" },
});

export interface RatingProps {
  className?: string;
  reviewCount?: number;
  score: number;
  size?: "20" | "24";
  variant?: "full" | "compact";
}

export function Rating({ className, reviewCount, score, size, variant }: RatingProps) {
  const styles = rating({ size, variant });

  if (variant === "compact") {
    return (
      <div className={styles.root({ className })}>
        <div className={styles.stars()}>
          <StickerStarIcon aria-hidden="true" className={styles.star()} />
        </div>
        <span className={styles.score()}>{score.toFixed(1)}</span>
        {/* eslint-disable-next-line i18next/no-literal-string -- parenthesized number, not translatable */}
        {reviewCount != null && <span className={styles.reviewCount()}>({reviewCount})</span>}
      </div>
    );
  }

  return (
    <div className={styles.root({ className })}>
      <div className={styles.stars()}>
        {Array.from({ length: Math.round(score) }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static decorative icons never reorder
          <StickerStarIcon key={i} aria-hidden="true" className={styles.star()} />
        ))}
      </div>
      {reviewCount != null && (
        <span className={styles.reviewCount()}>{t("components.rating.review_count", { count: reviewCount })}</span>
      )}
    </div>
  );
}
